<template>
  <div>

    <va-modal
      :value="isOpen"
      ref="examModal"
      :okText="$t('modals.buttons.save')"
      :okClass="'btn btn-primary btn-micro'"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('modals.buttons.back')"
      :cancelClass="'btn-cancelar btn btn-primary btn-micro'"
      @cancel="close"
      size="large">

      <!-- Modal Header -->
      <div slot="header" class="modal-title">
        <h4 class="mb-0">
          <b>{{ isUpdate ? $t('ranu.exams.update') : $t('ranu.exams.create') }}</b>
        </h4>
      <!-- End Modal Header -->
      </div>
      <div class="evollu-form">

        <div class="row">
          <!-- Performed By Input -->
          <div class="col-12 col-md-6">
            <va-input
              id="performed-by-input"
              v-model="form.data.performed_by"
              :maxlength="75"
              :label="$t('ranu.records.performed_by')"
              placeholder="---"
              :error="form.errors.has('performed_by')"
              :error-messages="[$t(form.errors.get('performed_by'))]"
              @focus="form.errors.clear('performed_by')"
            />
          </div>
          <!-- End Performed By Input -->

          <!-- Performed At Input -->
          <div class="col-12 col-md-6">
            <DatePicker
              id="date-picker-performed-at"
              placeholder="---"
              v-model="form.data.performed_at"
              :label="$t('ranu.records.performed_at')"
              :config="singleDateDatepickerConfig"
              @input="form.errors.clear('performed_at')"/>
            <small v-show="form.errors.has('performed_at')" class="help text-danger">
              {{ $t(form.errors.get('performed_at')) }}
            </small>
          </div>
          <!-- End Performed At Input -->
        </div>

        <div class="row">
          <!-- Form Process ID Input -->
          <div class="col-12 col-md-6">
            <va-input
              id="process-id-input"
              v-model="form.data.process_id"
              :maxlength="75"
              :label="$t('ranu.records.process_id')"
              placeholder="---"
              :error="form.errors.has('process_id')"
              :error-messages="[$t(form.errors.get('process_id'))]"
              @focus="form.errors.clear('process_id')"
            />
          </div>
          <!-- End Form Process ID Input -->

          <!-- Exam Type Multiselect -->
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('ranu.records.exam_type') }}</label>
            <multiselect
              id="exam-type-right-multiselect"
              v-model="form.data.exam_type"
              :options="availableExamTypes"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('exam_type')"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('exam_type')">
              <small>
                {{ $t(form.errors.get('exam_type')) }}
              </small>
            </div>
          </div>
          <!-- End Exam Type Multiselect -->
        </div>

        <div class="row">
          <!-- Result Left Multiselect -->
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('ranu.records.result_left') }}</label>
            <multiselect
              id="result-left-multiselect"
              v-model="form.data.result_left"
              :options="statusesOptions"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('result_left')"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('result_left')">
              <small>
                {{ $t(form.errors.get('result_left')) }}
              </small>
            </div>
          </div>
          <!-- End Result Left Multiselect -->

          <!-- Result Right Multiselect -->
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('ranu.records.result_right') }}</label>
            <multiselect
              id="result-right-multiselect"
              v-model="form.data.result_right"
              :options="statusesOptions"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              @input="form.errors.clear('result_right')"
              :disabled="rightResultIsDisabled"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
            <div class="text-danger" v-if="form.errors.has('result_right')">
              <small>
                {{ $t(form.errors.get('result_right')) }}
              </small>
            </div>
          </div>
          <!-- End Result Right Multiselect -->
        </div>

        <div class="row">
          <!-- Form Obs Input -->
          <div class="col-12">
            <va-input
              id="form-obs"
              v-model="form.data.obs"
              :maxlength="255"
              placeholder="---"
              :label="$t('ranu.records.obs')"
              @focus="form.errors.clear('obs')"
              :error="form.errors.has('obs')"
              :error-messages="[$t(form.errors.get('obs'))]"/>
          </div>
          <!-- End Form Obs Input -->
        </div>

        <!-- Upload files -->
        <div class="row">
          <div v-if="!form.file" class="col-12 text-center pt-5">
            <input ref="uploadFilesInput" id="upload-file-input" type="file" @input="onFileSelected" hidden
                  accept=".pdf, .png, .jpg, .jpeg">
            <va-button @click.prevent="uploadFile">
              <i class="fa fa-upload mr-1"></i> {{$t('ranu.records.upload_file')}}
            </va-button>
          </div>
          <div v-else class="col-12 text-center pt-5">
            <span class="font-weight-bold">{{form.file.name}}</span>
            <i class="fa fa-times ml-1 clickable" @click.prevent="removeFile"></i>
          </div>
          <small v-if="form.errors.has('file')" class="help text-danger">
            {{$t(form.errors.get('file'))}}
          </small>
        </div>
        <!-- End upload files -->
      </div>
    </va-modal>

  </div>

</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import Errors from '@/utils/StandardErrors.js'
import { ExamsService } from '@/api/RanuService'
import { mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import DatePicker from '@/components/common/DatePicker'

const recordsInfoType = 100
const readWriteAccessType = 2

export default {
  props: {
    record: {
      type: Object,
      required: true
    },
    exam: {
      type: Object,
      required: false,
    },
    availableExamTypes: {
      type: Array,
      required: true
    }
  },
  components: {
    DatePicker,
    Multiselect,
    ConfirmationModal
  },
  data () {
    return {
      isOpen: false,
      singleDateDatepickerConfig: {
        dateFormat: 'Y-m-d H:i',
        mode: 'single',
        maxDate: moment().format('YYYY-MM-DD HH:mm'),
        enableTime: true,
        time_24hr: true,
        minuteIncrement: 1,
        locale: 'pt'
      },
      rightResultIsDisabled: false,
      form: {
        data: {
          performed_by: null,
          process_id: null,
          performed_at: null,
          result_left: null,
          result_right: null,
          status: null,
          exam_type: null,
          obs: null,
        },
        file: null,
        errors: new Errors()
      },
      allowedExtensions: ['pdf', 'png', 'jpg', 'jpeg'],
    }
  },
  created: function () {
    if (this.exam) {
      this.prepareForUpdate()
    } else {
      this.fillForm()
    }
  },
  mounted: function () {
    this.isOpen = true
  },
  methods: {
    removeFile: function () {
      this.form.file = null
    },
    onFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      this.form.file = files[0]
    },
    uploadFile: function () {
      this.$refs.uploadFilesInput.click()
    },

    fillForm: function () {
      this.$nextTick(() => {
        if (this.availableExamTypes.length) {
          this.form.data.exam_type = this.availableExamTypes[0]
        }
        let previousRecordOnUserEntity = this.record.exams.find(exam => exam.entity !== null && exam.entity.id === this.$auth.user().entity_id)
        this.form.data.performed_by = this.$auth.user().title ? `${this.$auth.user().title} ${this.$auth.user().name}` : this.$auth.user().name
        this.form.data.performed_at = moment().format('YYYY-MM-DD HH:mm')
        this.form.data.process_id = previousRecordOnUserEntity ? previousRecordOnUserEntity.record.process_id : null
        if (this.form.data.process_id == null && this.record.exams.length === 0) {
          this.form.data.process_id = this.record.process_id
        }
        this.form.data.result_left = this.statusesOptions[0]
      })
    },
    submit: function () {
      if (this.form.file) {
        // ----- request to upload file ------------
        // -----------------------------------------
        let ranuFilesUrl = `${this.$store.state.ranu_server}/records/exams/files`
        let formData = new FormData()
        formData.append('file', this.form.file)
        this.$http.post(ranuFilesUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then((resp) => {
          let file = resp.data.data
          // ----- request to update/create exam -----
          // -----------------------------------------
          let data = this.getFormData()
          data.exam_files = [file.id]
          data.user_entity_id = this.$auth.user().entity_id
          this.handleUpdateCreateExam(data)
          // -----------------------------------------
          // -----------------------------------------
        }).catch((err) => {
          if (err.response.status === 422) {
            this.form.errors.record(err.response.data.errors, 'ranu.files.errors.')
          } else {
            this.showToast(this.$t('ranu.records.errors.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
      } else {
        let data = this.getFormData()
        data.exam_files = []
        data.user_entity_id = this.$auth.user().entity_id
        this.handleUpdateCreateExam(data)
      }
    },
    handleUpdateCreateExam: function (data) {
      let request = this.isUpdate ? ExamsService.update(this.record.id, this.exam.id, data) : ExamsService.store(this.record.id, data)
      request
        .then(response => {
          this.showToast(this.isUpdate ? this.$t('ranu.records.record_updated') : this.$t('ranu.records.record_created'), this.$store.state.successToastOptions)
          this.$emit(this.isUpdate ? 'updated' : 'created', response.data.data)
          this.$refs.examModal.cancel()
        })
        .catch(errors => {
          if (errors.response && errors.response.status === 422) {
            this.form.errors.record(errors.response.data.errors, 'ranu.records.errors.')
            this.showToast(this.$t('ranu.records.form_contains_errors'), this.$store.state.errorToastOptions)
          } else if (errors.response.status === 403) {
            this.showToast(this.$t('ranu.records.missing_store_permissions', this.$store.state.errorToastOptions))
          } else {
            this.showToast(this.$t('ranu.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.data))

      if (data.result_left) {
        data.result_left = data.result_left.id
      }
      if (data.result_right) {
        data.result_right = data.result_right.id
      }
      if (data.exam_type) {
        data.exam_type_id = data.exam_type.id
      }
      data.performed_at = moment(data.performed_at, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')

      return data
    },
    close: function () {
      this.isOpen = false
      if (!this.isDestroyingExam) {
        setTimeout(() => {
          this.$emit('modal-closed')
        }, 450)
      }
    },
    prepareForUpdate: function () {
      let formData = {
        ...JSON.parse(JSON.stringify(this.exam)),
      }
      this.form.data = formData
    }
  },
  computed: {
    ...mapState({
      statusesOptions: (state) => state.ranu.statusesOptions
    }),
    isUpdate: function () {
      return this.exam !== null
    },
    ranuService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ranu_server) || null
    },
    recordPermissions: function () {
      return this.ranuService.user_permissions.filter(permission => permission.access_type === readWriteAccessType && permission.info_type === recordsInfoType)
    },
    entityOptions: function () {
      if (this.recordPermissions.some(permission => permission.entity_id === null)) {
        return this.ranuService.entities
      } else {
        return this.ranuService.entities.filter(entity => this.recordPermissions.some(permission => permission.entity_id === entity.id))
      }
    },
    dateInterval: function () {
      if (!this.form.data.start_date || !this.form.data.end_date) {
        return ''
      }

      switch (this.currentLanguage) {
      case 'pt':
        return this.form.data.start_date + ' a ' + this.form.data.end_date
      case 'es':
        return this.form.data.start_date + ' a ' + this.form.data.end_date
      default:
        return this.form.data.start_date + ' to ' + this.form.data.end_date
      }
    },
    currentLanguage: function () {
      return Vue.i18n.locale()
    }
  },
  watch: {
    'form.data.result_left': function (value) {
      this.form.data.result_left = value
      if (value.applied_both) {
        this.form.data.result_right = value
      }
      this.rightResultIsDisabled = value.applied_both
    },
    'form.data.result_right': function (value) {
      this.form.data.result_right = value
      if (value.applied_both) {
        this.form.data.result_left = value
      }
      this.rightResultIsDisabled = value.applied_both
    }
  }
}
</script>

<style scoped lang="scss">

  .ion {
    font-size: 24px;
    line-height: 1.5rem;
    cursor: pointer;
  }

  label {
    justify-content: normal;
    font-size: 0.6rem;
    color: #29c4a9;
    font-weight: 600;
    text-transform: uppercase;
    top: -0.6rem;
    left: 0;
    margin-bottom: 0;
  }

  .multiselect--active {
    z-index: 11;
  }

  .form-group {
    margin-bottom: 0.65rem;
  }

  .form-group .input-group {
    align-self: flex-start;
  }

  .form-section {
    margin-left: 1rem;
    margin-right: 1.75rem;
    margin-bottom: 0.5rem;
  }

  .datepicker input {
    min-height: 40px;
    padding-left: 0.5rem;
    border: 1px solid #e8e8e8;
    background-color: #ffffff !important;
    border-radius: .4rem;
  }

  .cleardate-icon {
    color: #C0C0C0 !important;
    background-color: white;
    cursor: pointer;
    margin-left: 7px;
    margin-right: 4px;
    margin-top: 33px;
    margin-bottom: 2px;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    font-style: normal;
    width: 22px;
    text-align: center;
    line-height: 22px;
    border-radius: 5px;
  }

</style>
